/**
 * LaTeX mode for the CodeMirror source surfaces (Preview tab, validation).
 *
 * Deliberately mirrors the Prism palette used for Ask Lilia fences
 * (components/editor/LatexCode.tsx), because the same LaTeX appearing in two
 * places must not look like two different languages depending on which engine
 * happens to render it. Both resolve the same --lilia-code-* tokens; this file
 * decides which token each construct gets, so keeping the two in step is a
 * matter of keeping these tag choices aligned.
 *
 * The palette is ranked by how much meaning a construct carries:
 *
 *   tagName              environment / label / ref / cite / package NAMES —
 *                        the argument, not the command. The strongest colour,
 *                        because scanning for \begin{align} is the usual
 *                        reason to read LaTeX at all.
 *   string               math, tinted as a unit so a formula reads as one
 *                        object rather than a field of backslashes.
 *   processingInstruction commands inside math, separated from the math around
 *                        them, and \\ line breaks.
 *   comment              muted, italic.
 *   keyword              every other control sequence — the quietest coloured
 *                        token, since a document is mostly these.
 *   punctuation          braces, brackets, & and bare numbers. Neutral by
 *                        design: colouring punctuation is what turns syntax
 *                        colour into confetti, and it was the main thing
 *                        making this surface noisier than the Ask Lilia one.
 *
 * Greek letters and operator names used to get colours of their own (atom,
 * operatorKeyword). They are folded into the ordinary command colour now — at
 * seven colours the eye stops reading structure and starts reading decoration,
 * and neither distinction survives being next to real math.
 *
 * 11 Oct 2026 (the user: "LaTeX colouring can be enhanced further"), without
 * adding to the confetti:
 *   envKeyword           \begin and \end, the document's skeleton, one colour of
 *                        their own (the only new colour).
 *   math environments    equation, align, gather… and \[ \] \( \) tint as math,
 *                        like $…$ (they were prose before).
 *   heading              a \section/\chapter/\title argument, in weight, not colour.
 *   strong, emphasis     \textbf and \emph/\textit arguments, bold and italic.
 */
import { StreamLanguage, type StringStream } from "@codemirror/language";
import { tags } from "@lezer/highlight";

/** What the next braced argument holds. */
type ArgKind = "name" | "heading" | "strong" | "emphasis";

interface LatexState {
  inMathMode: boolean;
  /** In math because of a math environment (ends at its \end, not at a $). */
  mathEnv: boolean;
  /** Set when a command that takes a special argument was read; `{` starts it. */
  argKind: ArgKind | null;
  /** The special group being read and its brace depth (0: none). */
  groupKind: ArgKind | null;
  groupDepth: number;
  /** Reading the environment name of a \begin or \end. */
  envAction: "begin" | "end" | null;
  envName: string;
  /** Inside the [..] between a command and its argument. */
  optDepth: number;
}

/**
 * Commands whose braced argument names something the reader scans for.
 * \begin/\end carry the environment; the rest carry a label, key or package.
 */
const NAME_CMDS = new Set([
  "begin", "end", "label", "ref", "eqref", "pageref", "cite", "citep", "citet",
  "usepackage", "documentclass", "bibliography", "bibliographystyle", "input",
  "include", "includegraphics",
]);
const HEADING_CMDS = new Set(["part", "chapter", "section", "subsection", "subsubsection", "paragraph", "subparagraph", "title"]);
const STRONG_CMDS = new Set(["textbf"]);
const EMPHASIS_CMDS = new Set(["emph", "textit"]);
/** Environments whose body is math. */
const MATH_ENV = /^(equation|align|alignat|flalign|gather|multline|eqnarray|displaymath|math)\*?$/;

const GROUP_TOKEN: Record<ArgKind, string> = { name: "tagName", heading: "heading", strong: "strong", emphasis: "emphasis" };

export const latexLanguage = StreamLanguage.define<LatexState>({
  name: "latex",

  startState(): LatexState {
    return { inMathMode: false, mathEnv: false, argKind: null, groupKind: null, groupDepth: 0, envAction: null, envName: "", optDepth: 0 };
  },

  copyState(s: LatexState): LatexState {
    return { ...s };
  },

  token(stream: StringStream, state: LatexState): string | null {
    const inGroup = state.groupDepth > 0 && state.groupKind !== null;
    const groupToken = inGroup ? GROUP_TOKEN[state.groupKind!] : null;

    // Comment: % to end of line. Wins everywhere, including inside math.
    if (stream.match("%")) {
      stream.skipToEnd();
      return "comment";
    }

    // Math delimiters toggle the span. The delimiter itself is part of the
    // span so `$a^2$` tints end to end rather than showing bare $ markers.
    if (stream.match("$$") || stream.match("$")) {
      if (!state.mathEnv) state.inMathMode = !state.inMathMode;
      return "string";
    }

    // Line break.
    if (stream.match("\\\\")) {
      return "processingInstruction";
    }

    // Control sequence.
    if (stream.match("\\")) {
      const cmdMatch = stream.match(/^[a-zA-Z@]+/) as RegExpMatchArray | null;
      if (cmdMatch) {
        const cmd = cmdMatch[0];
        if (state.inMathMode) {
          // A math environment's \end closes the math.
          if (cmd === "end" && state.mathEnv) {
            state.inMathMode = false;
            state.mathEnv = false;
            state.argKind = "name";
            state.envAction = "end";
            state.envName = "";
            return "envKeyword";
          }
          // Inside math, commands take the math-command colour so they separate
          // from the math text around them.
          return "processingInstruction";
        }
        if (cmd === "begin" || cmd === "end") {
          state.argKind = "name";
          state.envAction = cmd;
          state.envName = "";
          return "envKeyword";
        }
        if (NAME_CMDS.has(cmd)) state.argKind = "name";
        else if (HEADING_CMDS.has(cmd)) state.argKind = "heading";
        else if (STRONG_CMDS.has(cmd)) state.argKind = "strong";
        else if (EMPHASIS_CMDS.has(cmd)) state.argKind = "emphasis";
        return "keyword";
      }
      // Escaped single character, e.g. \{ \} \, \& — or display/inline math.
      const ch = stream.next();
      if ((ch === "[" || ch === "(") && !state.inMathMode) {
        state.inMathMode = true;
        return "string";
      }
      if ((ch === "]" || ch === ")") && state.inMathMode && !state.mathEnv) {
        state.inMathMode = false;
        return "string";
      }
      return state.inMathMode ? "processingInstruction" : "keyword";
    }

    // Braces. Neutral, except that the opening brace after a command with a
    // special argument starts that run.
    if (stream.match("{")) {
      if (inGroup) {
        state.groupDepth++;
        state.argKind = null;
        return groupToken;
      }
      if (state.argKind) {
        state.groupKind = state.argKind;
        state.argKind = null;
        state.groupDepth = 1;
        return "punctuation";
      }
      return state.inMathMode ? "string" : "punctuation";
    }
    if (stream.match("}")) {
      if (inGroup) {
        state.groupDepth--;
        if (state.groupDepth > 0) return groupToken;
        // A math environment starts after its name.
        if (state.groupKind === "name" && state.envAction === "begin" && MATH_ENV.test(state.envName.trim())) {
          state.inMathMode = true;
          state.mathEnv = true;
        }
        state.groupKind = null;
        state.envAction = null;
        return "punctuation";
      }
      return state.inMathMode ? "string" : "punctuation";
    }

    // Optional-argument brackets. `[11pt,twoside]` sits between the command
    // and its name, so it must not clear the pending name.
    if (stream.match("[")) {
      if (state.argKind) state.optDepth++;
      return state.inMathMode ? "string" : "punctuation";
    }
    if (stream.match("]")) {
      if (state.optDepth > 0) state.optDepth--;
      return state.inMathMode ? "string" : "punctuation";
    }

    // Table / matrix cell separator.
    if (stream.match("&")) {
      return state.inMathMode ? "string" : "punctuation";
    }

    // Bare numbers. Uncoloured — they are almost always part of an argument
    // (11pt, level 2) and giving them their own hue adds noise, not meaning.
    if (stream.match(/^\d+\.?\d*/)) {
      if (groupToken) return groupToken;
      return state.inMathMode ? "string" : "punctuation";
    }

    // Content run: prose, titles, and the inside of a special group. Stops at
    // the next structural character. Tagged `content` outside math so the
    // source pane can keep rendering body prose in the document serif.
    const run = stream.match(/^[^\\%$&#{}[\]\r\n]+/) as RegExpMatchArray | null;
    if (run) {
      if (groupToken) {
        if (state.groupKind === "name" && state.envAction) state.envName += run[0];
        return groupToken;
      }
      // Words between a command and a `{`: that command took no argument.
      if (state.argKind && !state.optDepth && run[0].trim() && run[0].trim() !== "*") state.argKind = null;
      return state.inMathMode ? "string" : "content";
    }

    // Fallback single character.
    stream.next();
    if (groupToken) return groupToken;
    return state.inMathMode ? "string" : "content";
  },

  tokenTable: {
    comment: tags.comment,
    keyword: tags.keyword,
    envKeyword: tags.controlKeyword,
    string: tags.string,
    tagName: tags.tagName,
    heading: tags.heading,
    strong: tags.strong,
    emphasis: tags.emphasis,
    punctuation: tags.punctuation,
    processingInstruction: tags.processingInstruction,
    content: tags.content,
  },
});
